import { getOptionLabel, getOptions } from '@michelin/select-options-provider';
import uuid from 'uuid';
import {
  Contact,
  ContactListItem,
  ContactType,
  ContactTypeRole,
  SPPContactTypeLevels,
  SPPContactTypeLevelsEnum,
  SPPContactTypeRoleAreas,
  SPPContactTypeRoleAreasEnum,
  SPPContactTypeServices,
  SPPContactTypeServicesEnum,
} from '../../Types';
import { addContact, updateContact } from '../../hooks/useContactQueries';

export function hasMichelinContactTypes(contactTypes: ContactType[] | undefined, checkAssignedLevels: boolean = false): string[] {
  if (!contactTypes || !contactTypes.length) return [];
  const response: string[] = [];
  contactTypes.forEach((ct: ContactType) => {
    ct.role_areas.forEach((role: ContactTypeRole) => {
      if (role.role_area === SPPContactTypeRoleAreasEnum.pos_administrator) {
        if (!checkAssignedLevels) response.push((role).role_area);
        else if (role.levels[0].level !== SPPContactTypeLevelsEnum.general) response.push((role).role_area);
      }
    });
  });
  return response;
}

function hasContactTypePriorityLevels(contactTypes: ContactType[] | undefined): boolean {
  if (!contactTypes || !contactTypes.length) return false;
  // eslint-disable-next-line guard-for-in,no-restricted-syntax
  for (const i in contactTypes) {
    const roleAreas = contactTypes[i].role_areas;
    if (roleAreas.length) return true;
  }
  return false;
}

function hasContactTypePriorityLevelsAssigned(contactTypes: ContactType[] | undefined): boolean {
  if (!contactTypes || !contactTypes.length) return false;
  let response = false;
  contactTypes.forEach((contactType: ContactType) => {
    if (contactType.role_areas) {
      contactType.role_areas.forEach((area: ContactTypeRole) => {
        if (area.levels?.length && area.levels[0].level !== SPPContactTypeLevelsEnum.general) response = true;
      });
    }
  });
  return response;
}

export function hasContactTypeRoleLevel({
  data, services, roleAreas, levels,
}: { data: Contact, services?: SPPContactTypeServices[], roleAreas?: SPPContactTypeRoleAreas[], levels?: SPPContactTypeLevels[] }) {
  if (!data || !data.contact_types || !data.contact_types.length) return false;

  return data.contact_types.some((contactType) => {
    if (services !== undefined && !services.includes(contactType.service)) return false;
    if (!roleAreas && !levels) return true;
    return contactType.role_areas.some((role_area) => {
      if (!roleAreas?.includes(role_area.role_area)) return false;
      if (!levels) return true;
      return role_area.levels.some((lvl) => levels.includes(lvl.level));
    });
  });
}

/// //////////////////////////////////////////////////////////////////
/// Contacts Types
/// //////////////////////////////////////////////////////////////////

export interface ContactDetailsProps {
  action: string,
  onClose?: () => void,
  onSave?: (contact: Contact) => void,
  defaultData?: Contact,
  countryCode: string,
  basicFields?: boolean,
  contactId?: string,
}

function GetServiceLabel(service: string): string {
  let str = service;
  const contact_types_services: any = getOptions('dealer_dealer_contact_types_services');
  contact_types_services.forEach((v: any) => {
    if (v.value === service) str = v.label;
  });
  return str;
}

function GetContactTypeServiceLabel(contactType: ContactType): string {
  return GetServiceLabel(contactType.service);
}

function GetRoleAreaLabel(roleArea: string): string {
  return getOptionLabel('dealer_dealer_contact_types_role_areas', roleArea, roleArea);
}

function GetContactTypeRoleAreaLabel(role: ContactTypeRole): string {
  return GetRoleAreaLabel(role.role_area);
}

// TODO: Stop using this function
// TODO: Use getOptionLabel from Core Profile Import Scripts Library
function GetLevelLabel(level: string): string {
  let str = level;
  const contact_types_levels: any = getOptions('dealer_dealer_contact_types_levels');
  contact_types_levels.forEach((v: any) => {
    if (v.value === level) str = v.label;
  });
  return str;
}

function GetContactTypeLevelLabel(role: ContactTypeRole): string {
  const level = role.levels?.length ? role.levels[0].level : SPPContactTypeLevelsEnum.general;
  return GetLevelLabel(level);
}

function GetContactTypes(contact: Contact | ContactListItem): string[] {
  const res: string[] = [];

  if (!contact || !contact.contact_types) return [];
  contact.contact_types.forEach(
    (contactType) => contactType.role_areas?.forEach((roleAreas) => res.push(
      `${GetContactTypeServiceLabel(contactType)} ${GetContactTypeRoleAreaLabel(roleAreas)} ${
        roleAreas.levels[0].level !== SPPContactTypeLevelsEnum.general ? `[${GetContactTypeLevelLabel(roleAreas)}]` : ''}`,
    )),
  );

  return res;
}

function RetrieveRoleAreaPriorityLevel(contact: Contact | ContactListItem, service: string, roleArea: string): string | null {
  if (!contact || !contact.contact_types || !contact.contact_types.length) return null;
  const contactTypes = contact.contact_types;
  // eslint-disable-next-line no-restricted-syntax
  for (const i in contactTypes) {
    if (contactTypes[i].service !== service) continue;
    const roleAreas = contactTypes[i].role_areas;
    // eslint-disable-next-line no-restricted-syntax
    for (const j in roleAreas) {
      if (roleAreas[j].role_area !== roleArea) continue;
      if (roleAreas[j].levels?.length && roleAreas[j].levels[0].level) return roleAreas[j].levels[0].level;
    }
  }
  return null;
}

const getNewContact = (defaultData?: any): Contact => {
  const contact_id: string = uuid.v4();
  return {
    id: `${contact_id}`,
    contact_level: (defaultData?.contact_level) ? defaultData.contact_level : 'ST',
    first_name: (defaultData?.first_name) ? defaultData.first_name : '',
    last_name: (defaultData?.last_name) ? defaultData.last_name : '',
    job_title: (defaultData?.job_title) ? defaultData.job_title : '',
    email_address: (defaultData?.email_address) ? defaultData.email_address : '',
    cell_phone: (defaultData?.cell_phone) ? defaultData.cell_phone : '',
    work_phone: (defaultData?.work_phone) ? defaultData.work_phone : '',
    ext: (defaultData?.ext) ? defaultData.ext : '',
    fax: (defaultData?.fax) ? defaultData.fax : '',
    preferred_language: (defaultData?.preferred_language) ? defaultData.preferred_language : 'english',
    preferred_method: (defaultData?.preferred_method) ? defaultData.preferred_method : 'email',
    group: (defaultData?.group) ? defaultData.group : false,
    visible_externally: (defaultData?.visible_externally) ? defaultData.visible_externally : true,
    work_hours: (defaultData?.work_hours) ? defaultData.work_hours : {
      monday: { begin: undefined, end: undefined },
      tuesday: { begin: undefined, end: undefined },
      wednesday: { begin: undefined, end: undefined },
      thursday: { begin: undefined, end: undefined },
      friday: { begin: undefined, end: undefined },
      saturday: { begin: undefined, end: undefined },
      sunday: { begin: undefined, end: undefined },
    },
    contact_types: (defaultData?.contact_types) ? defaultData.contact_types : [],
    notification_subscription: (defaultData?.notification_subscription) ? defaultData.notification_subscription : [],
    certifications: (defaultData?.certifications) ? defaultData.certifications : [],
    outside_certifications: (defaultData?.outside_certifications) ? defaultData.outside_certifications : [],
  };
};

function completeContactTypes(contact_types: ContactType[], location_hash_key: string): ContactType[] {
  // eslint-disable-next-line no-restricted-syntax
  for (const contact_type of contact_types) {
    if (!contact_type.id) contact_type.id = uuid.v4();
    // eslint-disable-next-line no-restricted-syntax
    for (const role_area of contact_type.role_areas) {
      if (!role_area.levels || !role_area.levels.length) {
        // No levels for this role_area, add level and location
        role_area.levels.push({ level: SPPContactTypeLevelsEnum.general, location: location_hash_key });
      } else {
        // eslint-disable-next-line no-restricted-syntax
        for (const level of role_area.levels) {
          // Add location for empty general levels
          if (!level.location && level.level === SPPContactTypeLevelsEnum.general) level.location = location_hash_key;
        }
      }
    }
  }
  return contact_types;
}

async function manageContactCreation(locationNumber: string, contact: Contact) {
  // eslint-disable-next-line no-prototype-builtins,no-param-reassign
  if (contact.hasOwnProperty('hash_key')) delete (contact as any).hash_key;
  return addContact(locationNumber, contact);
}

async function manageContactUpdate(locationNumber: string, contactId: string, contact: Contact, basicFields: boolean) {
  // eslint-disable-next-line no-prototype-builtins,no-param-reassign
  if (contact.hasOwnProperty('hash_key')) delete (contact as any).hash_key;
  return updateContact(locationNumber, contactId, contact, basicFields);
}

async function manageContactSave({
  locationNumber,
  contact,
  contactId,
  basicFields,
}: { locationNumber: string, contact: Contact, contactId: string, basicFields: boolean }) {
  // eslint-disable-next-line no-prototype-builtins,no-param-reassign
  if (contact.hasOwnProperty('hash_key')) delete (contact as any).hash_key;
  // eslint-disable-next-line no-prototype-builtins,no-param-reassign
  if (contact.hasOwnProperty('id')) delete (contact as any).id;
  // eslint-disable-next-line no-param-reassign
  contact.contact_types = completeContactTypes(contact.contact_types || [], `10~${locationNumber}`);
  // eslint-disable-next-line no-param-reassign
  contact.group = contact.group || false;

  const commercialMechanicContact: boolean = hasContactTypeRoleLevel(
    {
      data: contact,
      services: [SPPContactTypeServicesEnum.commercial],
      roleAreas: [SPPContactTypeRoleAreasEnum.mechanic],
    },
  );
  const commercialTireTechnicianContact: boolean = hasContactTypeRoleLevel(
    {
      data: contact,
      services: [SPPContactTypeServicesEnum.commercial],
      roleAreas: [SPPContactTypeRoleAreasEnum.tire_technician],
    },
  );
  // eslint-disable-next-line no-param-reassign
  if (!commercialMechanicContact && !commercialTireTechnicianContact) contact.certifications = [];
  // eslint-disable-next-line no-param-reassign
  if (!commercialMechanicContact) contact.outside_certifications = [];

  try {
    let response;
    if (contactId) response = await manageContactUpdate(locationNumber, contactId, contact, basicFields);
    else response = await manageContactCreation(locationNumber, contact);
    return response;
  } catch (e) {
    return {
      status: 1,
      message: e.toString() || '',
      data: e,
    };
  }
}

export {
  GetServiceLabel,
  GetRoleAreaLabel,
  GetLevelLabel,
  GetContactTypes,
  RetrieveRoleAreaPriorityLevel,
  hasContactTypePriorityLevels,
  hasContactTypePriorityLevelsAssigned,
  getNewContact,
  manageContactSave,
};
